/**
 * Role constants and helpers for Atlas.
 *
 * Single source of truth for role names — never hard-code role strings
 * in components or route guards. Import from here so a rename is a one-line
 * change.
 *
 * Roles map 1:1 to Microsoft Entra ID App Roles. See CLAUDE.md →
 * "Microsoft Entra ID Setup → App Roles" for the canonical table.
 */

import {
  INITIATIVES_ADMIN_TIER,
  INITIATIVES_READ_TIER,
  INITIATIVES_WRITE_TIER,
} from '@jmtest/shared-types';

import { useAuth } from './useAuth';

export const ROLES = {
  /** All employees — read-only access to own records. Default baseline. */
  USER: 'Atlas.User',
  /** People managers — 1:1 scheduling, direct reports, team-level views. */
  MANAGER: 'Atlas.Manager',
  /** HR Director and HR Business Partners — full HR dashboard, master views, decision capture. */
  HR: 'Atlas.HR',
  /** HR Business Partners (subset of HR with employee-relations scope). */
  HRBP: 'Atlas.HRBP',
  /** Directors and above — exec dashboards, org-wide KPIs, calibration. */
  EXECUTIVE: 'Atlas.Executive',
  /** Full platform access — owner/admin views, configuration, content gap analytics. */
  ADMIN: 'Atlas.Admin',
  /** Legacy: explicit access to the Tech Progression form (manager-level workflow). */
  TECH_PROGRESSION: 'Tech_Progression',
  /** 6-Box Talent Assessment — sensitive HR data, gated to a small admin set
   *  before broader rollout to directors and managers. */
  SIX_BOX: 'Atlas.SixBox',
} as const;

export type Role = (typeof ROLES)[keyof typeof ROLES];

/**
 * Convenience role groups for common access patterns.
 * Use these rather than re-listing roles at every callsite.
 */
export const ROLE_GROUPS = {
  /** Anyone with HR-level visibility into the platform. */
  HR_TIER: [ROLES.HR, ROLES.HRBP, ROLES.ADMIN] as Role[],
  /** Anyone allowed to see exec-level org-wide views. */
  EXEC_TIER: [ROLES.EXECUTIVE, ROLES.ADMIN] as Role[],
  /** Anyone allowed into the Tech Progression admin master view (HR + Exec + Admin). */
  TECH_PROGRESSION_ADMIN: [
    ROLES.HR,
    ROLES.HRBP,
    ROLES.EXECUTIVE,
    ROLES.ADMIN,
  ] as Role[],
  /** Anyone allowed into the 6-Box talent module. Backed by the SIX_BOX role
   *  for primary gating, plus an email allowlist (server-side) until IT
   *  provisions the role for the rollout group. Admin always sees it. */
  SIX_BOX: [ROLES.SIX_BOX, ROLES.ADMIN] as Role[],
  /** Initiative Tracker — read the portfolio. Deliberately broad; sensitive
   *  rows are gated per-row by `visibility`, not by withholding the module.
   *
   *  These three tiers are re-exported from `@jmtest/shared-types` rather than
   *  re-listed, so the nav, the route guard and the API all read one
   *  definition. A nav item that offers a link the API denies is a documented
   *  P0 in this codebase — see `initiatives.roleTiers.test.ts`. */
  INITIATIVES_READ: [...INITIATIVES_READ_TIER] as Role[],
  /** Initiative Tracker — create initiatives, edit ones you own or sponsor.
   *  A coarse filter only: the API additionally allows any caller whose Entra
   *  OID matches the row's owner or sponsor. */
  INITIATIVES_WRITE: [...INITIATIVES_WRITE_TIER] as Role[],
  /** Initiative Tracker — edit any initiative, apply AI-proposed changesets,
   *  reassign owners, and read `restricted` rows. */
  INITIATIVES_ADMIN: [...INITIATIVES_ADMIN_TIER] as Role[],
} as const;

export interface UseRolesReturn {
  roles: string[];
  has: (role: string) => boolean;
  hasAny: (roles: readonly string[]) => boolean;
  hasAll: (roles: readonly string[]) => boolean;
  isAdmin: boolean;
  isHR: boolean;
  isExecutive: boolean;
  isManager: boolean;
  isUser: boolean;
  /** True for the Initiative Tracker admin tier (Executive or Admin). */
  isInitiativesAdmin: boolean;
}

/**
 * Hook returning the current user's roles plus convenience predicates.
 *
 * Returns empty/false values when the user is unauthenticated — safe to use
 * unconditionally in components that may render before auth resolves.
 */
export function useRoles(): UseRolesReturn {
  const { user } = useAuth();
  const roles = user?.roles ?? [];

  const has = (role: string): boolean => roles.includes(role);
  const hasAny = (candidates: readonly string[]): boolean =>
    candidates.some((r) => roles.includes(r));
  const hasAll = (candidates: readonly string[]): boolean =>
    candidates.every((r) => roles.includes(r));

  return {
    roles,
    has,
    hasAny,
    hasAll,
    isAdmin: has(ROLES.ADMIN),
    isHR: hasAny([ROLES.HR, ROLES.HRBP]),
    isExecutive: has(ROLES.EXECUTIVE),
    isManager: has(ROLES.MANAGER),
    isUser: has(ROLES.USER),
    // Reads the group rather than re-listing roles, so this predicate cannot
    // drift from the tier the nav and the API enforce. (Contrast `isHR` above,
    // which omits ADMIN and so does *not* match ROLE_GROUPS.HR_TIER — a known
    // inconsistency left alone here to avoid changing existing behaviour.)
    isInitiativesAdmin: hasAny(ROLE_GROUPS.INITIATIVES_ADMIN),
  };
}
